import {AxiosResponse} from 'axios';
import FileDownload from 'js-file-download';
import {compact, get, isUndefined, omitBy} from 'lodash-es';

import {IAdditionalLoadOptions} from 'components/form/inputs/reference-select';
import {URL_MENU} from 'components/property-settings/property-settings.constants';
import {
    PROPERTY_SETTINGS_ENTITY_NAME,
    SVC_RISK_ENTITY_NAME,
} from 'components/report-configuration/report-configuration.constants';
import {
    fetchEntityMetadata,
    IEntityMeta,
    IFieldMeta,
    IUrlConfig,
    selectMetadata,
    setMetadata,
} from 'entities/metadata';
import {WIDGET_PAGE_ENTITY_NAME} from 'pages/widget-page/widget-page';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages, EErrorMessages} from 'shared/constants/messages';
import {TStateSetter} from 'shared/types/generics';
import {ERequestType} from 'shared/types/requests';
import {
    convertToFormData,
    isArray,
    isObject,
    performRequest,
    showMessage,
    showMessageFromResponse,
} from 'shared/utils';
import {filterNonRenderableFields} from 'shared/utils/filter-non-renderable-fields';
import {editTableRows, fetchProperty} from 'shared/utils/request';
import {
    showMessageFromArrayResponse,
    showMessageFromDeleteRows,
    showMessageFromResponseList,
} from 'shared/utils/show-message-from-response';
import {createAction} from 'store/config/creators';
import {TAppState, TDispatch} from 'store/config/types';
import {resetForecastComments} from 'store/slices/ais-slice/ais-slice';
import {selectUserInfo} from 'store/slices/auth-slice';
import {IDocumentTypeResponse} from 'store/slices/documents-uploading-slice';
import {getBlankForm, normalizeEntityName} from 'utils/common';

import {selectContextRawData} from '../context/context-selector';
import {
    actionRequest,
    actionRequestWithJson,
    downloadFile,
    fetchDirectoryData,
    fetchForCheckConfirm,
    fetchFormData,
    fetchFormPageData,
    fetchReferenceData,
    fetchSimpleData,
    fetchTableData,
    postDirectoryData,
    postUploadFileAction,
    TOptionsActionRequest,
    updateNameDocument,
    uploadFileAdditional,
} from './data-api';
import {
    DataActions,
    DEFAULT_SEARCH_PARAMS,
    DEFAULT_TABLE_DATA,
    DEFAULT_TABLE_STATE,
    ENTITIES_FOR_REQUEST_WITH_CONTEXT,
    MANUALLY_UPDATING_CONTEXT_PAGES,
    OPTIONS_TABLE_DEFAULT,
    RESET_CONTEXT_ENTITIES,
} from './data-constants';
import {
    selectEntityData,
    selectFilterEntityData,
    selectFormEntityData,
    selectIsEntityDataLoading,
} from './data-selectors';
import {
    IActionsSendApiRequest,
    ICreateActionGetFile,
    ICreateActionHandlerForDelete,
    ICreateActionHandlerForTableChange,
    IPropertyData,
    IReferenceEntityData,
    IRemoveDocumentScansPayload,
    IRequestButtonBodyData,
    ISendSelectedRowIds,
    ISetDocumentScansDataPayload,
    TDataResetPayload,
    TDataSetFormModePayload,
    TDataSetInitialDataPayload,
    TDataSetIsEditablePayload,
    TDataSetPayload,
    TEntity,
    TEntityValue,
    TOptionsTableType,
    TResetFormDataPayload,
    TResetInitialTableDataArgs,
    TResetUsedAdditionalOptionIdPayload,
    TSetDataLoadingPayload,
    TSetFilterLoadingPayload,
    TSetFilterPayload,
    TSetFormInstancePayload,
    TSetSelectedSubsection,
    TSetUsedAdditionalOptionIdPayload,
    TSetUsedAdditionalOptionsPayload,
    TUpdateFormDataPayload,
} from './data-types';
import {
    addAdditionalInformationForData,
    concatObjectFromArray,
    convertMapToArray,
    convertParamsForRequest,
    createUrlAndMethod,
    generateLink,
    getDeleteInfoData,
    normalizeDataForProgramStages,
    normalizeDataForControlRatioRulesForm,
    normalizeFormDataForConfigurationForm,
    normalizeFormDataForRequest,
} from './utils/data.utils';

export const setData = createAction<TDataSetPayload>(DataActions.SET_DATA);
export const resetData = createAction<TDataResetPayload>(DataActions.RESET_DATA);
export const resetInitialData = createAction<TResetInitialTableDataArgs>(DataActions.RESET_INITIAL_DATA);
export const resetAllDataWithException = createAction<TDataResetPayload>(DataActions.RESET_ALL_DATA_WITH_EXCEPTION);
export const setDataIsEditable = createAction<TDataSetIsEditablePayload>(DataActions.SET_IS_EDITABLE);
export const updateFormData = createAction<TUpdateFormDataPayload>(DataActions.UPDATE_FORM_DATA);
export const resetFormData = createAction<TResetFormDataPayload>(DataActions.RESET_FORM_DATA);
export const setFilterLoading = createAction<TSetFilterLoadingPayload>(DataActions.SET_FILTER_LOADING_STATE);
export const setDataLoading = createAction<TSetDataLoadingPayload>(DataActions.SET_DATA_LOADING_STATE);
export const setFilter = createAction<TSetFilterPayload>(DataActions.SET_FILTER);
export const setFormInstance = createAction<TSetFormInstancePayload>(DataActions.SET_FORM_INSTANCE);
export const setInitialFormData = createAction<TDataSetInitialDataPayload>(DataActions.SET_INITIAL_FORM_DATA);
export const setFormUsedAdditionalOptionId = createAction<TSetUsedAdditionalOptionIdPayload>(
    DataActions.SET_FORM_USED_ADDITIONAL_OPTION_ID,
);
export const setFormUsedAdditionalOptions = createAction<TSetUsedAdditionalOptionsPayload>(
    DataActions.SET_FORM_USED_ADDITIONAL_OPTIONS,
);
export const resetFormUsedAdditionalOptions = createAction<TResetUsedAdditionalOptionIdPayload>(
    DataActions.RESET_FORM_USED_ADDITIONAL_OPTIONS,
);

export const setFormMode = createAction<TDataSetFormModePayload>(DataActions.SET_FORM_MODE);
export const setNotificationDuration = createAction<IPropertyData>(DataActions.SET_NOTIFICATION_DURATION);
export const setSelectedSubsection = createAction<TSetSelectedSubsection>(DataActions.SET_SELECTED_SUBSECTION);

export const setDocumentScans = createAction<ISetDocumentScansDataPayload>(
    DataActions.SET_DOCUMENT_SCANS_DATA,
);
export const removeDocumentScans = createAction<IRemoveDocumentScansPayload>(
    DataActions.REMOVE_DOCUMENT_SCANS,
);

export const loadReferenceData = (
    referenceUrl: string,
    additionalOptions?: IAdditionalLoadOptions,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    const loadingData = selectIsEntityDataLoading(
        referenceUrl,
    )(getState());
    const setReferenceData = (
        loading?: boolean, data?: IReferenceEntityData[], isError: boolean = false, innerPlaceholder?: string,
    ) => {
        let rows: any[] = data || [];
        if (rows) {
            rows = rows.map(item => {
                if (additionalOptions?.isSetFilter) {
                    const {isSetFilter} = additionalOptions;
                    item = {...item, isSetFilter};
                }
                if (additionalOptions?.displayFieldKey) {
                    const {displayFieldKey} = additionalOptions;
                    item = {...item, displayFieldKey};
                }
                if (additionalOptions?.dependentInputKey && !additionalOptions?.isSetFilter) {
                    const {dependentInputKey} = additionalOptions;
                    item = {...item, dependentInputKey};
                }
                if (additionalOptions?.onNotCreateKey) {
                    const {onNotCreateKey} = additionalOptions;
                    item = {...item, onNotCreateKey};
                }
                if (additionalOptions?.requestFieldKey) {
                    const {requestFieldKey} = additionalOptions;
                    item = {...item, requestFieldKey};
                }
                if (additionalOptions?.requestKey) {
                    const {requestKey} = additionalOptions;
                    item = {...item, requestKey};
                }
                return item;
            });

            if (additionalOptions?.additionalInputOptions) {
                const {additionalInputOptions} = additionalOptions;
                const newData: any[] = additionalInputOptions?.map(input => ({
                    lookupCode: input.key,
                    meaning: input.label,
                    value: input.value,
                    onNotCreateKey: input?.onNotCreateKey,
                    displayFieldKey: input?.displayFieldKey,
                    dependentInputKey: input.dependentInputKey,
                })) || [];
                rows = [...newData, ...rows];
            }
        }

        dispatch(setData({
            entityName: referenceUrl,
            entityType: EEntityType.REFERENCE,
            loading,
            isError,
            data: {
                rows,
                placeholder: innerPlaceholder,
            },
        }));
    };

    const result: { data: TEntity[] | null; error: any } = {data: null, error: null};

    if (loadingData) return result;

    try {
        setReferenceData(true);
        const fetchedData: any = await fetchReferenceData({
            referenceUrl,
            requestType: additionalOptions?.requestType,
        });
        if (isArray(fetchedData?.data)) {
            const {data: rows, response} = fetchedData ?? {};
            setReferenceData(false, rows, response?.status && response?.status !== 200);
            result.data = rows;
            if (response.status !== 200) result.error = response;
        } else {
            const {values, description: innerDescription} = fetchedData?.data ?? {};
            setReferenceData(
                false,
                values,
                fetchedData?.status,
                innerDescription,
            );
            result.data = values;
        }
    } catch (e: any) {
        if (e.response) {
            setReferenceData(false, [], true);
        }
        result.error = e?.response;
    }

    return result;
};

export const createLoadTableData = (entityType: EEntityType) => (
    entityName: string,
    options: TOptionsTableType = OPTIONS_TABLE_DEFAULT,
    url?: string,
    prefix?: string,
    urlConfig?: IUrlConfig,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    const getDataSlice = (type: EEntityType) => selectEntityData(entityName, type)(getState()) as Record<string, any>
        || {};
    const {
        useContext, useSearch, params, paramsToBeConverted, disabledPagination, sectionCode,
    } = options;
    const currentData = getDataSlice(entityType) ?? {};
    const userInfo = selectUserInfo(getState());
    const contextData = selectContextRawData(getState()) ?? {};
    let searchParameters;
    try {
        if (useSearch) {
            searchParameters = getDataSlice(EEntityType.SEARCH)?.data ?? DEFAULT_SEARCH_PARAMS;

            dispatch(setData({
                entityName,
                entityType: EEntityType.SEARCH,
                data: {
                    data: searchParameters,
                },
            }));
        }
        const convertedParams = paramsToBeConverted && convertParamsForRequest(paramsToBeConverted);
        const optionsParams = {entityName, sectionCode};
        const convertedContextParams = useContext ? convertParamsForRequest(contextData) : {};
        const combinedParams = concatObjectFromArray(compact([
            !disabledPagination && DEFAULT_TABLE_STATE,
            convertedContextParams,
            searchParameters,
            params,
            convertedParams,
            optionsParams,
        ]));

        if (RESET_CONTEXT_ENTITIES.includes(entityName)) {
            dispatch(resetForecastComments());
            return;
        }

        // отправлять запрос для страницы виджетов не нужно, страница настроек приложения подгружается по проперти
        if (entityName !== WIDGET_PAGE_ENTITY_NAME && entityName !== PROPERTY_SETTINGS_ENTITY_NAME) {
            // TODO: сделать отдельное поле метаданных для этого
            if (entityName === SVC_RISK_ENTITY_NAME) {
                combinedParams.taxPeriodId = undefined;
            }
            const {data: fetchedData} = await fetchTableData(
                entityType,
                entityName,
                combinedParams,
                url,
                userInfo?.userId,
                prefix,
                urlConfig,
            );
            let dataTable;
            let recordsTotal = 0;
            // TODO убрать условие Array.isArray(fetchedData) когда все api передут на новый формат
            if (Array.isArray(fetchedData)) {
                dataTable = fetchedData;
            } else if (isObject(fetchedData)) {
                dataTable = (fetchedData as Record<string, any>).dataTable;
                if (entityName === 'tax.accounting.income.report') {
                    dataTable = (fetchedData as Record<string, any>).content;
                }
                recordsTotal = (fetchedData as Record<string, any>).recordsTotal;
            }

            combinedParams.paginationTotal = recordsTotal;

            if (dataTable) {
                dispatch(setData({
                    entityName,
                    entityType,
                    loading: false,
                    data: {
                        ...DEFAULT_TABLE_DATA,
                        ...currentData,
                        params: combinedParams,
                        rows: [...dataTable],
                        initialRows: [...dataTable],
                        editRows: [],
                    },
                }));
            }
        }
        if (entityName === PROPERTY_SETTINGS_ENTITY_NAME) {
            await dispatch(loadReferenceData(URL_MENU));
        }
    } catch (e) {
        showMessageFromResponse({response: e.response, isError: true});
        console.error(e);
    }
};

export const loadTableData = createLoadTableData(EEntityType.TABLE);
export const loadFormTableData = createLoadTableData(EEntityType.FORM_TABLE);
export const loadEditableData = createLoadTableData(EEntityType.EDITABLE_TABLE);

export const resetLoadedData = (
    entityName: string,
    entityType: EEntityType = EEntityType.TABLE,
) => (dispatch: TDispatch) => {
    dispatch(resetData({
        entityType, entityName: normalizeEntityName(entityName), loading: false,
    }));
};

export const resetInitialTableData = (
    entityName: string,
    entityType: EEntityType = EEntityType.TABLE,
) => (dispatch: TDispatch) => {
    dispatch(resetInitialData({
        entityType, entityName: normalizeEntityName(entityName), loading: false,
    }));
};

export const loadDirectoryData = (
    entityName: string,
) => async (dispatch: TDispatch) => {
    const {data} = await fetchDirectoryData(entityName);
    try {
        dispatch(setData({
            entityName,
            entityType: EEntityType.TABS,
            data: {
                rows: data,
            },
        }));
    } catch (e) {
        console.error(e);
    }
};

export const loadDummyData = (
    entityName: string,
    entityType: EEntityType,
    propertyName: string,
) => async (dispatch: TDispatch) => {
    try {
        const {data} = await fetchSimpleData(entityName);
        dispatch(setData({
            entityName,
            entityType,
            data: {
                [propertyName]: data,
            },
        }));
    } catch (e) {
        console.error(e);
    }
};

interface IActionSaveDataArgs {
    requestUrl: string;
    requestType: ERequestType;
    data?: TEntity | Array<string | number>;
    options?: TOptionsActionRequest;
    successMessage?: string;
    errorMessage?: string;
    hideSuccessMessage?: boolean;
    headers?: Record<string, string>;
}

export const actionRequestData = async ({
    requestUrl,
    requestType,
    data = {},
    options = {},
    successMessage,
    errorMessage,
    hideSuccessMessage = false,
    headers = {},
}: IActionSaveDataArgs) => {
    try {
        const response = await actionRequest(
            requestUrl,
            requestType,
            data,
            options,
            undefined,
            undefined,
            headers,
        );
        if (!hideSuccessMessage) {
            if (successMessage) {
                showMessage({message: successMessage});
            } else {
                showMessageFromResponse({response});
            }
        }
        return response;
    } catch (e) {
        if (errorMessage) {
            showMessage({message: errorMessage, isError: true});
        } else {
            showMessageFromResponse({response: e.response, isError: true});
        }
        console.error(e);
        return e.response;
    }
};

export const loadNumberData = (
    url: string,
    entityName: string,
    key: string,
) => async (dispatch: TDispatch) => {
    const setNumberData = (loading?: boolean, data?: any, isError: boolean = false) => {
        dispatch(setData({
            entityName,
            entityType: EEntityType.NUMBER,
            loading,
            isError,
            data: {[key]: data},
        }));
    };
    try {
        setNumberData(true);
        const fetchedData: any = await performRequest({url});
        const {response, data} = fetchedData ?? {};
        setNumberData(false, data, response?.status && response?.status !== 200);
    } catch (e: any) {
        if (e.response) {
            setNumberData(false, null, true);
        }
    }
};

export const loadAdditionalFormData = (
    entityName: string,
    referenceUrl: string,
) => async (dispatch: TDispatch) => {
    try {
        const response: any = await fetchFormPageData(entityName, referenceUrl);
        if (response.status === 200) {
            const newData: any = response.data?.parameters;
            dispatch(setData({
                entityName,
                entityType: EEntityType.FORM,
                data: {
                    id: null,
                    initialData: {
                        ...newData,
                    },
                },
            }));
        }
    } catch (e) {
        showMessageFromResponse({response: e.response});
        console.error(e);
    }
};

export const saveDirectoryData = (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    entity: TEntity,
) => async (dispatch: TDispatch) => {
    try {
        const response = await postDirectoryData(referenceUrl, requestType, entity);

        if (response.status === 200) {
            dispatch(resetLoadedData(entityName, EEntityType.TABS));
        }

        showMessageFromResponse({response});
        return response;
    } catch (e) {
        console.error(e);
        return e.response;
    }
};

export const actionCreateListCP = (
    entityName: string,
    entityType: EEntityType,
    referenceUrl?: string,
    requestType?: ERequestType,
    data?: any,
) => async (dispatch: TDispatch) => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            params: convertParamsForRequest(data),
        });

        dispatch(setData({
            entityName,
            entityType,
            loading: false,
            data: {
                rows: response.data,
            },
        }));
        showMessageFromResponse({response});
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const publishListDocuments = (
    entityName: string,
    referenceUrl: string,
    idsList: {docId: string; comment?: string}[],
    createNews: boolean,
    publishDate: Date,
    sectionCodeContext: string,
    delayed?: boolean,
) => async (dispatch: TDispatch) => {
    try {
        const res = await performRequest({
            url: referenceUrl,
            method: ERequestType.POST,
            params: {
                sectionCodeContext,
                createNews,
                publishDate,
                delayed,
            },
            data: [...idsList],
        });
        if (res?.status === 200) {
            showMessageFromResponse({response: res, isError: false});
            dispatch(resetLoadedData(entityName, EEntityType.TABLE));
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const updateDocumentsStatus = (
    entityName: string,
    referenceUrl: string,
    newData: { docId: string | number; comment?: string }[],
    functionName: string,
    requestType?: ERequestType,
) => async (dispatch: TDispatch) => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            data: [...newData],
            params: {functionName},
        });
        if (response.status === 200) {
            showMessageFromResponse({response, isError: false});
            dispatch(resetData({
                entityName,
                entityType: EEntityType.TABLE,
            }));
            dispatch(resetLoadedData(entityName, EEntityType.TABLE));
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const sendSelectedRowIds = ({
    entityName,
    referenceUrl,
    requestType,
    selectedRowIds,
}: ISendSelectedRowIds) => async (dispatch: TDispatch) => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            data: selectedRowIds,
        });

        if (response.status === 200) {
            showMessageFromResponseList(response, false);
            dispatch(resetLoadedData(entityName, EEntityType.TABLE));
        }
    } catch (e) {
        showMessageFromResponseList(e.response, true);
        console.error(e);
    }
};

export const createActionGetFile = ({
    referenceUrl,
    requestType,
    data,
    isMessage,
    linkedEntityName,
    body,
    name,
    urlConfig,
    headers,
    requestMessages,
}: ICreateActionGetFile) => async (dispatch: TDispatch): Promise<AxiosResponse> => {
    const {successMessage, errorMessage} = requestMessages ?? {};
    try {
        const response = await performRequest({
            url: referenceUrl,
            params: data && convertParamsForRequest(data),
            responseType: 'blob',
            data: body,
            method: requestType,
            headers,
            urlConfig,
        });
        if (response.status === 200) {
            const fileName = name
                || decodeURI(response.headers['content-disposition'].split('filename=')[1]).replace(/"/g, '');
            FileDownload(response.data, fileName);

            if (isMessage || successMessage) {
                showMessageFromResponse({response, customMessage: successMessage ?? ECommonMessages.SAVE_SUCCESS});
            }
            if (linkedEntityName) {
                [EEntityType.TABLE, EEntityType.DASHBOARD].map(
                    entityType => dispatch(resetLoadedData(linkedEntityName, entityType)),
                );
            }
        }
        return response;
    } catch (e) {
        const errorText = await e?.response?.data?.text()?.then((error: string) => error);
        showMessageFromResponse({response: undefined, isError: true, customMessage: errorMessage ?? errorText});
        return e.response;
    }
};

export const createActionGetFileAndOpenInNewTab = (
    referenceUrl?: string,
    requestType?: ERequestType,
    data?: any,
    isMessage?: boolean,
) => async (): Promise<AxiosResponse> => {
    try {
        const response = await performRequest({
            baseURL: `${window.location.origin}/nalmon/`,
            url: referenceUrl,
            method: requestType,
            params: data && convertParamsForRequest(data),
            responseType: 'blob',
        });

        if (response.status === 200) {
            const file = new Blob([response.data], {type: 'application/pdf'});
            if (isMessage) {
                showMessageFromResponse({response, isError: false, customMessage: ECommonMessages.SAVE_SUCCESS});
            }
            const fileURL = URL.createObjectURL(file);
            const pdfWindow = window.open();
            if (pdfWindow) {
                pdfWindow.location.href = fileURL;
            }
        }
        return response;
    } catch (e) {
        console.error(e);
        return e.response;
    }
};

export const actionSendApiRequest = ({
  referenceUrl,
  requestType,
  data,
}: IActionsSendApiRequest) => async () => {
  try {
      return await performRequest({
        url: referenceUrl,
        method: requestType,
        data,
    });
  } catch (e: any) {
    console.error(`Возникла ошибка при выполнении запроса ${referenceUrl}:`, e);
    throw e;
  }
};

export const actionsRequestButton = (
    referenceUrl?: string,
    requestType?: ERequestType,
    params: any = {},
    data?: IRequestButtonBodyData,
    urlConfig?: IUrlConfig,
) => async () => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            params: convertParamsForRequest(params),
            data,
            urlConfig,
        });

        showMessageFromResponse({response});
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};
type TActionHandlerOptionsType = {
    /**
     * Добавляет информационные поля о создании и изменении
     */
    needChangesInfoForNormalizedData?: boolean;
    /**
     * Отвечает за привидение данных к общему виду для определенной сущности
     */
    normalizedDataForEntityType?: EEntityType;
    /**
     * Тело запроса будет в формате JSON, вместо FormData
     */
    isJsonRequest?: boolean;
    shouldUsedFileIndexName?: boolean;
}

export const performActionForFormTable = (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    formatedData: TEntity,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId;
        const entitiesTypesToReset = [EEntityType.FORM_TABLE];
        const response = await actionRequestWithJson(
            referenceUrl,
            requestType,
            formatedData,
            userId,
        );

        if (response.status === 200) {
            entitiesTypesToReset.forEach(entityType => {
                dispatch(resetLoadedData(entityName, entityType));
            });
        }
        showMessageFromResponse({response});
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const saveEditableData = (
    entityName: string,
    entityType: EEntityType,
    requestType: ERequestType,
    newData: TEntity,
    isJsonRequest?: boolean,
    referenceUrl?: string,
    noResetData?: boolean,
    shouldResetInitData?:boolean,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        let keyForId = 'id';
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId;
        const fieldsToNormalize: IFieldMeta[] = [];
        const {fields, linkField} = selectMetadata(entityName, entityType)(getState());
        fieldsToNormalize.push(...fields);
        keyForId = linkField || keyForId;
        const normalizedData = normalizeFormDataForRequest(newData, fieldsToNormalize, keyForId, isJsonRequest);
        const response = await actionRequest(
            `${referenceUrl || entityName}/${newData.id}`,
            requestType,
            normalizedData,
            {
                isJsonRequest,
            },
            userId,
        );
        if (response.status === 200 && !noResetData) {
            dispatch(resetLoadedData(entityName));
        } else {
            if (shouldResetInitData) {
                dispatch(resetInitialTableData(entityName));
            }
            showMessageFromResponse({response});
        }
    } catch (e) {
        console.error(e.error);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const saveMultipleEditableData = (
    entityName: string,
    entityType: EEntityType,
    requestType: ERequestType,
    newData: TEntity[],
    referenceUrl: string,
    isJsonRequest?: boolean,
    noResetData?: boolean,
    shouldResetInitData?:boolean,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        let keyForId = 'id';
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId;
        const fieldsToNormalize: IFieldMeta[] = [];
        const {fields, linkField} = selectMetadata(entityName, entityType)(getState());
        fieldsToNormalize.push(...fields);
        keyForId = linkField || keyForId;
        const normalizedData = newData.map(
            data => (normalizeFormDataForRequest(data, fieldsToNormalize, keyForId, isJsonRequest)),
        );

        const response = await actionRequest(
            referenceUrl,
            requestType,
            normalizedData,
            {
                isJsonRequest,
            },
            userId,
        );
        if (response.status === 200 && !noResetData) {
            dispatch(resetLoadedData(entityName));
        } else {
            if (shouldResetInitData) {
                dispatch(resetInitialTableData(entityName));
            }
            showMessageFromResponse({response});
        }
    } catch (e) {
        console.error(e.error);
        showMessageFromResponse({response: e.response, isError: true});
        throw e.error;
    }
};

export const createActionHandlerForDelete = ({
    entityName,
    referenceUrl,
    requestType,
    itemList,
    itemListKey = 'ids',
    entityTypes = [EEntityType.TABLE, EEntityType.DASHBOARD],
    useUrl = false,
    urlConfig,
}: ICreateActionHandlerForDelete) => async (dispatch: TDispatch) => {
    try {
        let url = referenceUrl;
        if (useUrl) {
            const values = get(itemList, itemListKey);
            url = `${referenceUrl}/${values}`;
        }
        const response = await performRequest({
            url,
            method: requestType,
            data: !useUrl ? itemList : undefined,
            urlConfig,
        });

        if (response.status === 200) {
            entityTypes.map(entityType => dispatch(resetLoadedData(entityName, entityType)));
        }
        showMessageFromDeleteRows(response);
    } catch (e) {
        console.error(e);
        showMessageFromDeleteRows(e.response, true);
    }
};

export const createActionHandlerForReportConfigSave = (
    entitiesTypesToReset: EEntityType[],
    {
        needChangesInfoForNormalizedData = false,
        normalizedDataForEntityType,
        shouldUsedFileIndexName = false,
    }: TActionHandlerOptionsType,
) => (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    newData: TEntity = {},
    additionalRequestKeys: string[],
    isJsonRequest?: boolean,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId || '1';
        let normalizedData = newData;
        let keyForId = 'templateCode';
        const fieldsToNormalize: IFieldMeta[] = [];

        if (normalizedDataForEntityType) {
            const {
                fields, linkField,
            } = selectMetadata(entityName, normalizedDataForEntityType)(getState());
            fieldsToNormalize.push(...fields);
            keyForId = linkField || keyForId;
            normalizedData = normalizeFormDataForConfigurationForm(
                newData, fieldsToNormalize, keyForId, additionalRequestKeys, isJsonRequest,
            );
        }

        if (needChangesInfoForNormalizedData) {
            const {
                discardDateAndUserInfo,
            } = selectMetadata(entityName, normalizedDataForEntityType || EEntityType.FORM)(getState());
            normalizedData = addAdditionalInformationForData(normalizedData, userId, keyForId, discardDateAndUserInfo);
        }

        let url = referenceUrl;
        if (requestType === ERequestType.PUT) {
            url = `${referenceUrl}/${normalizedData[keyForId]}`;
        }

        const response: any = await actionRequest(url, requestType as ERequestType, normalizedData, {
            isJsonRequest, shouldUsedFileIndexName,
        }, userId);

        // todo: нужно разбивать дату на 2 отдельных запроса, подождать фикс на бэке
        // const {templateName, templateCode, ...rest} = normalizedData;
        // const templateResponse = await actionRequest(url, requestType as RequestType, {templateName, templateCode}, {
        //     isJsonRequest, shouldUsedFileIndexName,
        // }, userId);
        // const configResponse = await actionRequest(url, requestType as RequestType, rest, {
        //     isJsonRequest, shouldUsedFileIndexName,
        // }, userId);

        // if (response.status === 200) {
        //     entitiesTypesToReset.forEach(entityType => {
        //         dispatch(resetLoadedData(entityName, entityType));
        //     });
        // }

        if (response.isAxiosError && response.response) {
            return response.response;
        }
        showMessageFromResponse({response});
        return response;
    } catch (e) {
        if (entitiesTypesToReset[0] === EEntityType.FORM) {
            return e.response;
        }
        showMessageFromResponse({response: e.response, isError: true});
        return e.response;
    }
};

export const actionForRequests = (
    entityName: string,
    requestType: ERequestType,
    referenceUrl: string,
    ids?: string[],
) => async (dispatch: TDispatch) => {
    try {
        const res = await performRequest({
            url: referenceUrl,
            method: requestType,
            data: ids ? {ids} : undefined,
        });
        if (res?.status === 200) {
            showMessageFromResponse({response: res, isError: false});
            dispatch(resetLoadedData(entityName, EEntityType.TABLE));
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

interface ICreateActionHandlerForReportConfigurationSaveArgs {
    url: string;
    data: any;
    requestType: ERequestType;
}

export const createActionHandlerForReportConfigurationSave = ({
    data,
    requestType,
    url,
}: ICreateActionHandlerForReportConfigurationSaveArgs) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId || '1';
        const keyForId = 'templateCode';

        const requestUrl = (() => {
            if (requestType === ERequestType.PUT) return `${url}/${data[keyForId]}`;
            return url;
        })();

        const response: any = await actionRequest(requestUrl, requestType as ERequestType, data, {
            isJsonRequest: true,
            shouldUsedFileIndexName: false,
        }, userId);

        if (response.isAxiosError && response.response) {
            return response.response;
        }
        showMessageFromResponse({response});
        return response;
    } catch (e: any) {
        showMessageFromResponse({response: e.response, isError: true});
        return e.response;
    }
};

interface ICreateActionHandlerForPropertySettingsSaveArgs {
    data: any;
    url?: string;
}

export const createActionHandlerForPropertySettingsSave = ({
    data,
    url = 'administration.property/rpc/update-list',
}: ICreateActionHandlerForPropertySettingsSaveArgs) => async () => {
    try {
        const response = await performRequest({
            url,
            method: 'POST',
            data: omitBy(data, isUndefined),
        });
        showMessageFromArrayResponse(response);
        return response;
    } catch (e: any) {
        showMessageFromResponse({response: e.response, isError: true});
        return e.response;
    }
};

export const createActionHandlerForHeader = (
    entitiesTypesToReset: EEntityType[],
    {
        needChangesInfoForNormalizedData = false,
        normalizedDataForEntityType,
        shouldUsedFileIndexName = false,
    }: TActionHandlerOptionsType,
) => (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    newData: TEntity = {},
    isJsonRequest?: boolean,
    urlParamKey: string = '',
    additionalData: TEntity = {},
    urlConfig?: IUrlConfig,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        let response: any = {};
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId || '1';
        let normalizedData = newData;
        let keyForId = 'id';
        const fieldsToNormalize: IFieldMeta[] = [];

        if (normalizedDataForEntityType) {
            const {
                fields, linkField,
            } = selectMetadata(entityName, normalizedDataForEntityType)(getState());
            fieldsToNormalize.push(...fields);
            keyForId = linkField || keyForId;
            normalizedData = normalizeFormDataForRequest(
                newData,
                fieldsToNormalize,
                keyForId,
                isJsonRequest,
                ENTITIES_FOR_REQUEST_WITH_CONTEXT.includes(entityName),
            );
        }
        if (needChangesInfoForNormalizedData) {
            const {
                discardDateAndUserInfo,
            } = selectMetadata(entityName, normalizedDataForEntityType || EEntityType.FORM)(getState());
            normalizedData = addAdditionalInformationForData(
                normalizedData,
                userId,
                keyForId,
                discardDateAndUserInfo,
                additionalData,
            );
        }
        const deleteItem = getDeleteInfoData(newData, fieldsToNormalize, keyForId);
        const [url, method] = createUrlAndMethod(
            referenceUrl,
            requestType,
            normalizedData[keyForId],
            normalizedData[urlParamKey],
        );

        if (deleteItem && deleteItem.isDelete && deleteItem.referenceUrl) {
            const {ids} = deleteItem;
            if (ids) {
                response = await performRequest({
                    url: deleteItem.referenceUrl,
                    method: requestType,
                    data: ids,
                    urlConfig,
                });

                if (response.status === 200) {
                    entitiesTypesToReset.forEach(entityType => {
                        dispatch(resetLoadedData(entityName, entityType));
                    });
                }

                return response;
            }
            return undefined;
        }
        // TODO весь метод - костыль для сохранения данных с Файлов с индексацией
        const normalizedShouldUsedFileIndexName: boolean = normalizedData?.shouldUsedFileIndexName as boolean;

        // TODO: костыль для сохранения в разделе "Определение этапов для наборов программ"
        if (entityName === 'administration.program.set.stages'
            && (method === ERequestType.PUT || method === ERequestType.POST)) {
            normalizedData = normalizeDataForProgramStages(normalizedData);
        }
        
        // костыль для сохранения в разделе "Настройка правил контрольных соотношений и правил сборки"
        if (entityName === 'administration.settings.control.ratios.rules'
            && (method === ERequestType.PUT || method === ERequestType.POST)) {
            normalizedData = normalizeDataForControlRatioRulesForm(normalizedData);
        }

        // для формы из раздела "Формирование ответа на требование"
        // в столбце "Информация о невозможности представления документов" убираем id из тела запроса
        if (entityName === 'response.requirement.inability.info'
            && (method === ERequestType.PUT || method === ERequestType.DELETE)) {
            delete normalizedData.id;
        }

        response = await actionRequest(url, method as ERequestType, normalizedData, {
            isJsonRequest, shouldUsedFileIndexName, normalizedShouldUsedFileIndexName,
        }, userId, urlConfig);

        if (method === ERequestType.GET && response.status === 200) {
            const fileName = decodeURI(response.headers['content-disposition'].split('filename=')[1].replace(/"/g, ''));
            FileDownload(response.data, fileName);
            return response;
        }

        if (response.status === 200) {
            entitiesTypesToReset.forEach(entityType => {
                dispatch(resetLoadedData(entityName, entityType));
            });
        }

        if (response.isAxiosError && response.response) {
            return response.response;
        }
        showMessageFromResponse({response});
        return response;
    } catch (e) {
        if (entitiesTypesToReset[0] === EEntityType.FORM) {
            return e.response;
        }
        showMessageFromResponse({response: e.response, isError: true});
        return e.response;
    }
};

export const createActionHandlerDownloadFile = (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    itemList: TEntity = {},
    urlConfig?: IUrlConfig,
) => async () => {
    try {
        const url = referenceUrl;

        const response: AxiosResponse = await performRequest({
            url,
            method: requestType,
            data: itemList,
            responseType: 'blob',
            urlConfig,
        });

        handleResponse(response);
    } catch (error) {
        handleError(error);
    }

    async function handleResponse(response: AxiosResponse) {
        if (response.status === 200) {
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const fileName = extractFileName(contentDisposition);
                FileDownload(response.data, fileName);
            } else {
                showErrorMessage(response);
            }
        } else if (response.status === 400) {
            showErrorMessage(response);
        }
    }

    async function handleError(error: { response: AxiosResponse<any> }) {
        showErrorMessage(error.response);
        console.error(error);
    }

    function extractFileName(contentDisposition: string): string {
        const decoded = decodeURI(contentDisposition);
        return decoded.split('filename=')[1]?.trimStart() || 'unknown_file';
    }

    async function showErrorMessage(response: AxiosResponse) {
        const errorText = await extractErrorText(response);
        showMessageFromResponse({response, isError: true, customMessage: errorText});
    }

    async function extractErrorText(response: AxiosResponse): Promise<string> {
        return response?.data?.text?.() || EErrorMessages.UNEXPECTED_ERROR_OCCURRED;
    }
};

export const checkConfirm = (
    entityName: string,
    referenceUrl: string,
    paramContext: any,
) => async (dispatch: TDispatch) => {
    try {
        const params = convertParamsForRequest(paramContext);
        const response = await fetchForCheckConfirm(referenceUrl, params);
        dispatch(setData({
            entityName,
            entityType: EEntityType.TABLE,
            loading: false,
            data: {
                isConfirmed: response.data,
            },
        }));
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const createActionConfirm = (
    entityName: string,
    referenceUrl?: string,
    requestType?: ERequestType,
    data?: any,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    try {
        const contextData = selectContextRawData(getState()) ?? {};
        await performRequest({
            url: referenceUrl,
            method: requestType,
            data: convertToFormData(convertParamsForRequest(data)),
        }).then(res => {
            showMessageFromResponse({response: res, isError: false});
            dispatch(checkConfirm(
                entityName,
                `${entityName}/isConfirmed`,
                contextData,
            ));
        });
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};
export const createActionLastPeriod = (
    referenceUrl?: string,
    requestType?: ERequestType,
    data?: any,
) => async () => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            data: convertToFormData(convertParamsForRequest(data)),
        });
        if (response.status === 200) {
            showMessageFromResponse({response, isError: false});
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const actionSaveRows = (
    entityName: string,
    referenceUrl: string,
    requestType: ERequestType,
    itemList: any,
) => async (dispatch: TDispatch) => {
    try {
        const response = await editTableRows(
            requestType,
            referenceUrl || entityName,
            itemList,
        );

        const {processingResultList} = response.data;

        if (response.status === 200 || processingResultList[0].status === 'SUCCESS') {
            dispatch(resetLoadedData(entityName, EEntityType.TABLE));
            showMessageFromResponse({response, isError: false});
        } else {
            showMessageFromResponse({response, isError: true});
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};
export const performActionForForm = (
    entityTypeToNormalize: EEntityType = EEntityType.FORM,
) => createActionHandlerForHeader(
    [EEntityType.FORM, EEntityType.TABLE, EEntityType.TABS, EEntityType.TABS_WITH_FORM, EEntityType.DASHBOARD],
    {
        normalizedDataForEntityType: entityTypeToNormalize,
        needChangesInfoForNormalizedData: true,
    },
);

// todo: for report configuration form
export const performActionForReportConfigForm = createActionHandlerForReportConfigSave(
    [EEntityType.FORM, EEntityType.TABLE, EEntityType.TABS, EEntityType.TABS_WITH_FORM],
    {
        normalizedDataForEntityType: EEntityType.FORM,
        needChangesInfoForNormalizedData: false,
    },
);

export const performActionForTable = createActionHandlerForHeader(
    [EEntityType.TABLE],
    {
        normalizedDataForEntityType: EEntityType.FORM,
        needChangesInfoForNormalizedData: false,
        isJsonRequest: false,
    },
);

export const performSimpleActionForTable = createActionHandlerForHeader(
    [EEntityType.TABLE],
    {
        isJsonRequest: true,
    },
);

export const createActionHandlerForTableChange = (entityType: EEntityType) => ({
    entityName,
    newEntityTypeData,
    url,
    parentEntityType = EEntityType.TABLE,
    prefix,
    disabledPagination,
    skipIfFilterDataIsLoading,
    urlConfig,
}: ICreateActionHandlerForTableChange) => async (dispatch: TDispatch, getState: () => TAppState) => {
    const getMetadataSlice = (type: EEntityType) => selectMetadata(entityName, type)(
        getState(),
    ) || {} as IEntityMeta;
    const {useContext} = getMetadataSlice(parentEntityType);
    if (!prefix) {
        prefix = getMetadataSlice(parentEntityType)?.prefix;
    }
    const getDataSlice = (type: EEntityType) => selectEntityData(entityName, type)(getState()) as Record<string, any>
        || {};
    try {
        const {data: oldEntityTypeData} = getDataSlice(entityType);
        dispatch(setData({
            entityName,
            entityType,
            loading: true,
            data: {
                data: {
                    ...oldEntityTypeData,
                    ...newEntityTypeData,
                    search: newEntityTypeData.search?.toString().trim(),
                },
            },
        }));
        const searchData = getDataSlice(EEntityType.SEARCH)?.data ?? DEFAULT_SEARCH_PARAMS;
        const {data: tableStateData} = getDataSlice(EEntityType.TABLE_STATE);
        const filterSlice = selectFilterEntityData(entityName)(getState());
        const isNeedResetData = parentEntityType !== EEntityType.WIDGETS
            && parentEntityType !== EEntityType.TABLE_REPORT
            && !MANUALLY_UPDATING_CONTEXT_PAGES.includes(entityName);
        const loadTableOptions = {
            useContext: !!useContext,
            paramsToBeConverted: {
                ...tableStateData,
                ...filterSlice?.data,
                // reset page if filter is changed, bad solution should be rewritten when RTK Query will be implemented
                paginationCurrent: entityType === EEntityType.FILTER
                || entityType === EEntityType.SEARCH ? 1 : tableStateData?.paginationCurrent,
                ...searchData,
            },
            disabledPagination,
            ...newEntityTypeData,
        };
        // не обновлять данные таблицы, пока фильтры еще загружаются
        if ((!skipIfFilterDataIsLoading || !filterSlice.loading) && isNeedResetData) {
            await dispatch(
                createLoadTableData(parentEntityType)(
                    entityName,
                    loadTableOptions,
                    url,
                    prefix,
                    urlConfig,
                ),
            );
        }
        // TODO: Добавить параметры, когда реализуют изменение инф. дашбордов по фильтрам таблицы
        await dispatch(resetLoadedData(entityName, EEntityType.DASHBOARD));
    } catch (e) {
        showMessageFromResponse({response: e.response, isError: true});
    }

    return null;
};

export const performActionForFilter = createActionHandlerForTableChange(EEntityType.FILTER);

export const performActionForSearch = createActionHandlerForTableChange(EEntityType.SEARCH);

export const performActionForTableState = createActionHandlerForTableChange(EEntityType.TABLE_STATE);

export const loadFormData = (
    entityName: string,
    id: string,
    url?: string,
    params?: any,
    urlConfig?: IUrlConfig,
) => async (dispatch: TDispatch) => {
    try {
        const {data} = await fetchFormData(entityName, id, url, params, urlConfig);
        dispatch(setData({
            entityName,
            entityType: EEntityType.FORM,
            data: {id, data},
        }));
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const loadReportConfigFormData = (
    entityName: string,
    id: string,
    params?: any,
) => async (dispatch: TDispatch) => {
    try {
        const {data: templateData} = await fetchFormData(entityName, '', `table-reports/${id}`, params);
        const {
            queryParameters, reportHeaders, reportColumns, reportMenu,
        } = templateData;
        const data = {
            ...templateData,
            queryParameters: convertMapToArray(queryParameters),
            reportHeaders: convertMapToArray(reportHeaders),
            reportColumns: convertMapToArray(reportColumns),
            reportMenu: reportMenu?.map((item: any) => ({
                ...item, reportColumns: convertMapToArray(item.reportColumns),
            })),
        };
        dispatch(setData({
            entityName,
            entityType: EEntityType.FORM,
            data: {templateCode: id, data},
        }));
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const loadPropertyFormData = (
    entityName: string,
    id: string,
) => async (dispatch: TDispatch) => {
    try {
        const {data} = await fetchFormData(entityName, id);
        dispatch(setData({
            entityName: `${entityName}/${id}`,
            entityType: EEntityType.FORM,
            data: {propertyCode: id, data},
        }));
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};

export const loadFormPageData = (entityName: string, url?: string) => async (
    dispatch: TDispatch,
    getState: () => TAppState,
) => {
    try {
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId;
        const {data} = await fetchFormPageData(entityName, url, undefined, undefined, userId);
        dispatch(setData({
            entityName,
            entityType: EEntityType.FORM,
            data: {data, initialData: data},
        }));
    } catch (e) {
        showMessageFromResponse({response: e.response, isError: true});
        console.error(e);
    }
};

export const loadEditFormData = (
    entityName: string,
    url?: string,
    prefix?: string,
    urlConfig?: IUrlConfig,
) => async (
    dispatch: TDispatch,
    getState: () => TAppState,
) => {
    try {
        const pathId = url?.split('/').slice(-1)[0];
        const getEntityUrl = prefix ? `${prefix}/${pathId}` : url;
        const userInfo = selectUserInfo(getState());
        const userId = userInfo?.userId;
        const {data} = await fetchFormPageData(
            entityName,
            getEntityUrl,
            undefined,
            undefined,
            userId,
            urlConfig,
        );
        dispatch(setData({
            entityName,
            entityType: EEntityType.FORM,
            data: {data, initialData: data},
        }));
    } catch (e) {
        showMessageFromResponse({response: e.response, isError: true});
        console.error(e);
    }
};

export const initializeMetadataAndData = (entityType: EEntityType) => (
    entityName: string,
    defaultMetadata?: IEntityMeta,
    defaultData?: TEntityValue,
) => async (
    dispatch: TDispatch, getState: () => TAppState,
) => {
    try {
        let formMetadata = selectMetadata(entityName, entityType)(getState());
        const shouldUpdateMetadata = !formMetadata;

        if (shouldUpdateMetadata) {
            formMetadata = defaultMetadata || (await fetchEntityMetadata(entityName, entityType))?.data;
            dispatch(setMetadata({entityName, entityType, metadata: formMetadata}));
        }

        let formData = selectEntityData(entityName, entityType)(getState());

        if (entityType === EEntityType.FORM) {
            formData = selectFormEntityData(entityName)(getState())?.data;
        }

        const shouldUpdateData = !formData;
        if (shouldUpdateData) {
            const {fields = []} = formMetadata;
            formData = defaultData || getBlankForm(fields, getState);
            dispatch(setData({
                entityName,
                entityType,
                data: {
                    id: null,
                    data: formData,
                    initialData: formData,
                },
            }));
        }
    } catch (e) {
        console.error(e);
    }
};
export const editNameDocument = (
    entityName: string,
    url: string,
    id: string,
    requestType: ERequestType,
    data: any,
    entityType: EEntityType,
    params: Record<string, any>,
) => async (dispatch: TDispatch) => {
    try {
        const response = await updateNameDocument(
            `${url}/${id}`,
            requestType,
            data,
        );

        if (response.status === 200) {
            showMessageFromResponse({response, isError: false});
            await dispatch(performActionForTableState({
                entityName,
                newEntityTypeData: params,
                url,
            }));
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};
export const loadAdditionalDataFile = (
    entityName: string,
    referenceUrl: string,
    file: FormData,
    params?: any,
) => async (dispatch: TDispatch) => {
    try {
        const convertedParams = params ? convertParamsForRequest(params) : null;
        const {data} = await uploadFileAdditional(referenceUrl, file, convertedParams);
        dispatch(setData({
            entityName,
            entityType: EEntityType.FORM,
            data: {
                data,
                initialData: data,
            },
        }));
    } catch (e) {
        console.error(e);
    }
};
export const uploadFileAction = (
    entityName: string,
    referenceUrl: string,
    file: FormData,
    params?: any,
    correctUploadMessage?: string,
) => async (dispatch: TDispatch) => {
    try {
        const data = await postUploadFileAction(referenceUrl, file, params);
        if (data.status === 200) {
            showMessageFromResponse({response: data, isError: false, customMessage: correctUploadMessage});
            [EEntityType.TABLE, EEntityType.DASHBOARD].map(
                entityType => dispatch(resetLoadedData(entityName, entityType)),
            );
        }
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};
export const initBlankForm = initializeMetadataAndData(EEntityType.FORM);

export const downloadFileForUrl = (referenceUrl: string | undefined) => async () => {
    if (typeof referenceUrl !== 'string') {
        return;
    }
    downloadFile(generateLink(referenceUrl)).then(data => {
        if (data.status === 200) {
            showMessageFromResponse({
                response: data,
                isError: false,
                customMessage: ECommonMessages.DOWNLOAD_FILE_SUCCESS,
            });
        }
    }).catch(error => {
        showMessageFromResponse({response: error?.response, isError: true});
    });
};

export const filterNonRenderableFieldsAndUpdateState = (
    metadata: IEntityMeta,
    entityName: string,
    entityType: EEntityType,
    setFilteredFlag: TStateSetter<boolean>,
    branchNames?: IDocumentTypeResponse[],
    setDefaultBranchName?: TStateSetter<IDocumentTypeResponse | undefined>,
) => (dispatch: TDispatch, getState: () => TAppState) => {
    const userInfo = selectUserInfo(getState());
    const renderableFields = filterNonRenderableFields(metadata.fields, userInfo);
    setFilteredFlag(true);
    // Костыль, чтобы не перетирались значения, когда мы при открытии сразу подтягиваем fields доп. запросом
    if (!metadata?.additionalOptions?.optionsField?.isChangingFormStructure) {
        dispatch(setMetadata({
            entityName,
            entityType,
            metadata: {...metadata, fields: renderableFields},
        }));
    }
    if (setDefaultBranchName && branchNames) {
        const defaultBranch = branchNames.find(branch => branch?.attribute1 === 'MAIN');
        setDefaultBranchName(defaultBranch);
    }
};

export const getValueByPropertyCode = (
    propertyCode?: string,
) => async () => {
    if (propertyCode) {
        try {
            const response = await fetchProperty(propertyCode);
            return response?.data;
        } catch (error) {
            showMessage({
                message: error.response.data,
                isError: true,
            });
        }
    }
    return undefined;
};

export const formationRequestAction = async (
    selectedRowKeys: (string|number)[],
    referenceUrl: string,
    formationStartMessage: string,
    extraErrorMessage: string,
) => {
    showMessage({
        message: formationStartMessage,
    });

    try {
        const response = await performRequest({
            url: referenceUrl,
            method: 'POST',
            data: {
                ids: selectedRowKeys,
            },
        });
        showMessage({
            message: response.data,
        });
    } catch (error) {
        showMessage({
            message: `"${(error.response?.data.split(' : ')[1] ?? extraErrorMessage).slice(1, -1)}"`,
            isError: true,
        });
    }
};

export const copyResponseRequestAction = (
    referenceUrl?: string,
    requestType?: ERequestType,
    params: any = {},
    data?: IRequestButtonBodyData,
    urlConfig?: IUrlConfig,
) => async () => {
    try {
        const response = await performRequest({
            url: referenceUrl,
            method: requestType,
            params: convertParamsForRequest(params),
            data,
            urlConfig,
        });

        showMessageFromResponse({response});
    } catch (e) {
        console.error(e);
        showMessageFromResponse({response: e.response, isError: true});
    }
};